import { useCallback, useEffect, useMemo } from 'react';

import clsx from 'clsx';

import useGa from '@analytics/hooks/useGa';
import useEventTypesStore from '@event/store/useEventTypesStore';
import { useTutorialStore } from '@event/store/useTutorialStore';
import { EventTimetableOption, EventType } from '@event/types';
import usePopupStore from '@popup/stores/usePopupStore';
import ListSelector from '@selector/components/ListSelector';
import Button from '@ui/components/Button';
import IconArrow from '@ui/components/icons/IconArrow';
import useClientStore from '@utils/hooks/useClientStore';

const TimeslotLengthSelector = () => {
  const {
    selectedEventType,
    setSelectedEventType,
    selectedEventTimeslotLength,
    setSelectedEventTimeslotLength,
    eventsType,
    setEventsType,
  } = useEventTypesStore();
  const {
    popupState: { isTimeLengthSlotSelectorOpen },
    setPopupState,
  } = usePopupStore();
  const logToGa = useGa();

  const handleTimeslotChange = useCallback(
    (
      timetableOption: EventTimetableOption,
      eventType: EventType,
      withReordering = false,
    ) => {
      const updatedTimetableOptions = eventType.timetableOptions.map(
        (option) =>
          option.id === timetableOption.id
            ? { ...option, isSelected: true }
            : { ...option, isSelected: false },
      );

      const updatedEventType = {
        ...eventType,
        timetableOptions: updatedTimetableOptions,
      };

      const updatedEventTypes = eventsType.map((item) =>
        item.id === updatedEventType.id
          ? updatedEventType
          : { ...item, isSelected: false },
      );

      if (withReordering) {
        updatedEventType.timetableOptions.sort((a) => (a.isSelected ? -1 : 1));
      }

      setEventsType(updatedEventTypes);
      setSelectedEventType(updatedEventType);
      setSelectedEventTimeslotLength(timetableOption);

      logToGa({
        event: 'button_click',
        button: {
          text: timetableOption.name,
          type: 'event length',
          phase: 'planning',
        },
      });
    },
    [
      eventsType,
      setEventsType,
      setSelectedEventType,
      setSelectedEventTimeslotLength,
      logToGa,
    ],
  );

  useEffect(() => {
    const actualSelectedEventTimeslot =
      selectedEventType?.timetableOptions.find((option) => option.isSelected)!;

    setSelectedEventTimeslotLength(actualSelectedEventTimeslot);
  }, [selectedEventType, setSelectedEventTimeslotLength]);

  const handleModalClick = (optionName: string) => {
    if (selectedEventType) {
      const selectedOption = selectedEventType?.timetableOptions.find(
        (option) => option.name === optionName,
      )!;

      handleTimeslotChange(selectedOption, selectedEventType, true);
    }

    setPopupState({ isTimeLengthSlotSelectorOpen: false });
  };

  const firstTwoSelectedEventTypes = useMemo(() => {
    return selectedEventType?.timetableOptions.slice(0, 2);
  }, [selectedEventType]);

  const tutorialStore = useClientStore(useTutorialStore, (state) => state);

  return (
    <div className="ml-6 flex flex-row items-center first-of-type:ml-0">
      {firstTwoSelectedEventTypes &&
        selectedEventType &&
        firstTwoSelectedEventTypes.map((option) => (
          <Button
            key={option.name}
            disabled={option.id === selectedEventTimeslotLength?.id}
            isActive={option.id === selectedEventTimeslotLength?.id}
            onClick={() => {
              handleTimeslotChange(option, selectedEventType);
            }}
            className="whitespace-nowrap"
            classNameWrapper="mr-2"
          >
            {option.name}
          </Button>
        ))}
      <div
        className={clsx(
          'ml-auto',
          tutorialStore?.isTutorialActive && 'relative',
        )}
      >
        <Button
          addonBefore={
            <IconArrow
              className={clsx(
                'fill-brand-gray-100',
                isTimeLengthSlotSelectorOpen && 'rotate-180',
              )}
            />
          }
          onClick={() =>
            setPopupState({
              isTimeLengthSlotSelectorOpen: !isTimeLengthSlotSelectorOpen,
            })
          }
          className="ignore-click-outside"
        />
        {isTimeLengthSlotSelectorOpen && (
          <ListSelector
            handleClick={handleModalClick}
            handleClose={() =>
              setPopupState({ isTimeLengthSlotSelectorOpen: false })
            }
            eventTitle={selectedEventTimeslotLength?.name || ''}
            listData={
              selectedEventType?.timetableOptions
                .slice(2)
                .map((option) => option.name) || []
            }
          />
        )}
      </div>
    </div>
  );
};

export default TimeslotLengthSelector;
